import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IntegrationStatus, useListAgents, useListIntegrationsV2 } from '@api';
import { Category } from '@organisms/CatalogV2';
import { kababToCapitalCase } from '@utils';
import GenericFilterMultiSelect from '@common/organisms/CommonFilters/GenericFilterMultiSelect';
import ListFilters from '@common/ui/ListFilters/ListFilters';

export const CATEGORY_FILTER_LABEL = 'Category';
export const CATEGORY_FILTER_PARAM = 'category';
export const CONNECTOR_FILTER_LABEL = 'Connector';
export const CONNECTOR_FILTER_PARAM = 'connector';
export const STATUS_FILTER_LABEL = 'Status';
export const STATUS_FILTER_PARAM = 'status';
export const TYPE_FILTER_LABEL = 'Type';
export const TYPE_FILTER_PARAM = 'type';

const DEFAULT_VISIBLE_FILTERS = [CATEGORY_FILTER_PARAM, TYPE_FILTER_PARAM, STATUS_FILTER_PARAM, CONNECTOR_FILTER_PARAM];

function useIntegrationFilterOptions() {
  const {
    integrations,
    isIntegrationsFetched: isIntegrationsAndConfigsFetchedV2,
    configs,
  } = useListIntegrationsV2(true);
  const { connectors, isConnectorsFetched } = useListAgents();
  const filterOptions = useMemo(() => {
    if (!isIntegrationsAndConfigsFetchedV2 || !isConnectorsFetched) {
      return { category: [], type: [], status: [], connector: [] };
    }
    const categories = new Set<string>();
    const types = new Set<string>();
    const connectorsList = new Set<string>();
    configs.forEach((config) => {
      categories.add(config.category);
    });
    integrations.forEach((integration) => {
      types.add(integration.type);
      if (integration.provisioner_id) {
        connectorsList.add(integration.provisioner_id);
      }
    });
    const categoryOptions = Array.from(categories).map((c) => ({
      label: Category[c as keyof typeof Category],
      value: c,
    }));
    const typeOptions: { label: string; value: string }[] = [];
    types.forEach((type) => {
      const integrationType = configs.find((c) => c.type === type);
      if (integrationType) {
        typeOptions.push({
          label: kababToCapitalCase(integrationType.name),
          value: type,
        });
      }
    });
    typeOptions.sort((a, b) => a.label.localeCompare(b.label));
    const statusOptions = Object.values(IntegrationStatus).map((s) => ({
      label: s,
      value: s,
    }));
    const connectorOptions: { label: string; value: string }[] = [];
    connectorsList.forEach((connector) => {
      const provisioner = connectors.find((c) => c.agent_id === connector);
      connectorOptions.push({
        label: provisioner?.name || 'Connector',
        value: connector,
      });
    });
    connectorOptions.sort((a, b) => a.label.localeCompare(b.label));
    return {
      category: categoryOptions,
      type: typeOptions,
      status: statusOptions,
      connector: connectorOptions,
    };
  }, [isIntegrationsAndConfigsFetchedV2, configs, isConnectorsFetched, connectors]);
  return { filterOptions };
}

export function ConnectedIntegrationFilters() {
  const [openedFilter, setOpenedFilter] = useState<string | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();

  const { filterOptions } = useIntegrationFilterOptions();

  const onCloseFilter = () => setOpenedFilter(undefined);
  const onOpenFilter = (value: string) => setOpenedFilter(value);

  const filters = useMemo(
    () => [
      {
        name: CATEGORY_FILTER_LABEL,
        queryParam: CATEGORY_FILTER_PARAM,
        element: (
          <GenericFilterMultiSelect
            opened={openedFilter === CATEGORY_FILTER_PARAM}
            onClose={onCloseFilter}
            onOpen={() => onOpenFilter(CATEGORY_FILTER_PARAM)}
            filterParam={CATEGORY_FILTER_PARAM}
            options={filterOptions.category}
            filterLabel={CATEGORY_FILTER_LABEL}
          />
        ),
      },
      {
        name: TYPE_FILTER_LABEL,
        queryParam: TYPE_FILTER_PARAM,
        element: (
          <GenericFilterMultiSelect
            opened={openedFilter === TYPE_FILTER_PARAM}
            onClose={onCloseFilter}
            onOpen={() => onOpenFilter(TYPE_FILTER_PARAM)}
            filterParam={TYPE_FILTER_PARAM}
            options={filterOptions.type}
            filterLabel={TYPE_FILTER_LABEL}
          />
        ),
      },
      {
        name: STATUS_FILTER_LABEL,
        queryParam: STATUS_FILTER_PARAM,
        element: (
          <GenericFilterMultiSelect
            opened={openedFilter === STATUS_FILTER_PARAM}
            onClose={onCloseFilter}
            onOpen={() => onOpenFilter(STATUS_FILTER_PARAM)}
            filterParam={STATUS_FILTER_PARAM}
            options={filterOptions.status}
            filterLabel={STATUS_FILTER_LABEL}
          />
        ),
      },
      {
        name: CONNECTOR_FILTER_LABEL,
        queryParam: CONNECTOR_FILTER_PARAM,
        element: (
          <GenericFilterMultiSelect
            opened={openedFilter === CONNECTOR_FILTER_PARAM}
            onClose={onCloseFilter}
            onOpen={() => onOpenFilter(CONNECTOR_FILTER_PARAM)}
            filterParam={CONNECTOR_FILTER_PARAM}
            options={filterOptions.connector}
            filterLabel={CONNECTOR_FILTER_LABEL}
          />
        ),
      },
    ],
    [openedFilter, onCloseFilter, onOpenFilter, filterOptions.category, filterOptions.status, filterOptions.type],
  );

  const hasActiveFilters = useMemo(() => {
    for (const filter of filters) {
      if (searchParams.has(filter.queryParam)) {
        return true;
      }
    }
    return false;
  }, [filters, searchParams]);

  const handleOnClear = useCallback(() => {
    for (const filter of filters) {
      searchParams.delete(filter.queryParam);
    }
    setSearchParams(searchParams);
  }, [filters, searchParams, setSearchParams]);

  const handleOnOnAddFilter = (qp: string) => {
    setOpenedFilter(qp);
  };

  return (
    <ListFilters
      visibleFilters={DEFAULT_VISIBLE_FILTERS}
      filters={filters}
      onAddFilter={handleOnOnAddFilter}
      onClear={hasActiveFilters ? handleOnClear : undefined}
    />
  );
}
